import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  makeStyles,
} from '@material-ui/core';
import {
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Clear as ClearIcon,
} from '@material-ui/icons';
import { useAppState } from '../state/AppProvider';
import {
  Conversation,
  historyDelete,
  historyClear,
  historyRename,
  historyRead,
} from '../api';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  activeContainer: {
    backgroundColor: theme.palette.action.selected,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontWeight: 500,
    fontSize: '14px',
    marginBottom: theme.spacing(0.5),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    flex: 1,
  },
  date: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
  },
  menuButton: {
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
  },
}));

interface ChatHistoryListItemProps {
  conversation: Conversation;
}

export const ChatHistoryListItem: React.FC<ChatHistoryListItemProps> = ({
  conversation,
}) => {
  const classes = useStyles();
  const { state, dispatch } = useAppState();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState(conversation.title);

  const isActive = state.currentConversation?.id === conversation.id;

  const handleClick = async (event: React.MouseEvent) => {
    // Don't trigger if clicking on menu button
    if ((event.target as HTMLElement).closest('.menu-button')) {
      return;
    }

    try {
      // Load conversation messages
      const messages = await historyRead(discoveryApi, fetchApi, conversation.id);
      
      dispatch({
        type: 'SET_CURRENT_CONVERSATION',
        payload: {
          ...conversation,
          messages,
        },
      });
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRename = () => {
    setNewTitle(conversation.title);
    setRenameDialogOpen(true);
    handleMenuClose();
  };

  const handleRenameConfirm = async () => {
    try {
      const response = await historyRename(
        discoveryApi,
        fetchApi,
        conversation.id,
        newTitle,
      );
      
      if (response.ok) {
        dispatch({
          type: 'UPDATE_CONVERSATION_TITLE',
          payload: { id: conversation.id, title: newTitle },
        });
      }
    } catch (error) {
      console.error('Error renaming conversation:', error);
    } finally {
      setRenameDialogOpen(false);
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await historyDelete(discoveryApi, fetchApi, conversation.id);
      
      if (response.ok) {
        dispatch({
          type: 'DELETE_CHAT_ENTRY',
          payload: conversation.id,
        });
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleClear = () => {
    setClearDialogOpen(true);
    handleMenuClose();
  };

  const handleClearConfirm = async () => {
    try {
      const response = await historyClear(discoveryApi, fetchApi, conversation.id);
      
      if (response.ok) {
        // Update conversation with empty messages
        dispatch({
          type: 'UPDATE_CONVERSATION',
          payload: {
            ...conversation,
            messages: [],
          },
        });
        
        // If this is the current conversation, clear it
        if (isActive) {
          dispatch({
            type: 'SET_CURRENT_CONVERSATION',
            payload: null,
          });
        }
      }
    } catch (error) {
      console.error('Error clearing conversation:', error);
    } finally {
      setClearDialogOpen(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <>
      <Box
        className={`${classes.container} ${isActive ? classes.activeContainer : ''}`}
        onClick={handleClick}
      >
        <Box className={classes.header}>
          <Box flex={1}>
            <Typography className={classes.title}>
              {conversation.title}
            </Typography>
            <Typography className={classes.date}>
              {formatDate(conversation.date)}
            </Typography>
          </Box>
          <IconButton
            className={`${classes.menuButton} menu-button`}
            size="small"
            onClick={handleMenuOpen}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleRename}>
          <EditIcon fontSize="small" style={{ marginRight: 8 }} />
          Rename
        </MenuItem>
        <MenuItem onClick={handleClear}>
          <ClearIcon fontSize="small" style={{ marginRight: 8 }} />
          Clear messages
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Rename Conversation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                handleRenameConfirm();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRenameConfirm} color="primary">
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this conversation? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Dialog */}
      <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)}>
        <DialogTitle>Clear Messages</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to clear all messages in this conversation? The
            conversation will be kept but all messages will be removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleClearConfirm} color="secondary">
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

